'use client'

import { useEffect, useState } from 'react'
import { HugeiconsIcon } from '@hugeicons/react'
import { Invoice01Icon } from '@hugeicons-pro/core-stroke-rounded'
import type { PostPayStatus } from '@/app/api/wallet/post-pay-status/status-response'

const INVOICE_TILE = 'linear-gradient(135deg, #0A8A6E, #2BC39A)'

function formatMoney(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

function formatDate(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function statusClass(status: NonNullable<PostPayStatus['last_invoice_status']>): string {
  if (status === 'paid') return 'bg-emerald-50 text-emerald-700'
  if (status === 'overdue') return 'bg-red-50 text-red-700'
  return 'bg-amber-50 text-amber-700'
}

function labelStatus(status: NonNullable<PostPayStatus['last_invoice_status']>): string {
  if (status === 'paid') return 'Paid'
  if (status === 'overdue') return 'Overdue'
  return 'Pending'
}

export function PostPayActiveSummary() {
  const [status, setStatus] = useState<PostPayStatus | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    fetch('/api/wallet/post-pay-status')
      .then((r) => (r.ok ? r.json() : null))
      .then((data: PostPayStatus | null) => {
        if (cancelled) return
        setStatus(data)
        setLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  if (loading || !status) return null

  const lastInvoiceValue =
    status.last_invoice_date && status.last_invoice_amount_cents != null
      ? `${formatMoney(status.last_invoice_amount_cents)} on ${formatDate(status.last_invoice_date)}`
      : null

  return (
    <div className="bv3-brow" style={{ alignItems: 'flex-start', marginBottom: 8 }}>
      <div className="ic" style={{ background: INVOICE_TILE }}>
        <HugeiconsIcon icon={Invoice01Icon} className="size-5" />
      </div>

      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 15, fontWeight: 700 }}>Post-pay billing</div>
        <div className="mt-3 divide-y divide-atrium-border">
          <div className="flex items-center justify-between gap-4 py-2 first:pt-0">
            <div>
              <div style={{ fontSize: 12, color: '#888' }}>Current cycle balance</div>
              <div style={{ fontSize: 14, fontWeight: 700, marginTop: 2 }}>
                {formatMoney(status.current_balance_owed_cents)}
              </div>
            </div>
          </div>

          {status.next_invoice_date ? (
            <div className="flex items-center justify-between gap-4 py-2">
              <div>
                <div style={{ fontSize: 12, color: '#888' }}>Next invoice</div>
                <div style={{ fontSize: 14, fontWeight: 700, marginTop: 2 }}>
                  {formatDate(status.next_invoice_date)}
                </div>
              </div>
            </div>
          ) : null}

          {lastInvoiceValue ? (
            <div className="flex items-center justify-between gap-4 py-2 last:pb-0">
              <div>
                <div style={{ fontSize: 12, color: '#888' }}>Last invoice</div>
                <div style={{ fontSize: 14, fontWeight: 700, marginTop: 2 }}>
                  {lastInvoiceValue}
                </div>
              </div>
              {status.last_invoice_status ? (
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${statusClass(status.last_invoice_status)}`}>
                  {labelStatus(status.last_invoice_status)}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}
